import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@mainstage/core-plugin-api';
import { ResponseError } from '@mainstage/errors';
import { z } from 'zod';

import {
  mcpToolReferenceZod,
  paginatedResponseZod,
  PaginationRequest,
  toQueryString,
  wittyUuidZod,
} from './schema';

export const wittySkillZod = z.object({
  id: wittyUuidZod,
  platform_key: z.string().nullable(),
  name: z.string(),
  description: z.string(),
  content: z.string(),
  mcp_tools: z.array(mcpToolReferenceZod),
  mcp_servers: z.array(z.string().min(1)),
  agent_count: z.number().int().nonnegative(),
  used_by_agents: z.array(
    z.object({
      id: wittyUuidZod,
      label: z.string(),
    }),
  ),
  is_platform_provided: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type WittySkill = z.infer<typeof wittySkillZod>;

export const wittySkillSummaryZod = wittySkillZod
  .omit({
    content: true,
    mcp_tools: true,
    mcp_servers: true,
    used_by_agents: true,
  })
  .extend({ tool_count: z.number().int().nonnegative() });

export type WittySkillSummary = z.infer<typeof wittySkillSummaryZod>;

export type WittySkillListRequest = PaginationRequest & {
  is_platform_provided?: boolean;
  ids?: string[];
  sort_by?: 'name' | 'description' | 'type' | 'agent_count' | 'tool_count';
  sort_order?: 'asc' | 'desc';
};

export const wittySkillUpdateZod = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().optional(),
  content: z.string().min(1).optional(),
  mcp_tools: z.array(mcpToolReferenceZod).optional(),
  mcp_servers: z.array(z.string().min(1)).optional(),
});

export type WittySkillUpdate = z.infer<typeof wittySkillUpdateZod>;

export const wittySkillCreateZod = z.object({
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  content: z.string().min(1),
  mcp_tools: z.array(mcpToolReferenceZod).optional(),
  mcp_servers: z.array(z.string().min(1)).optional(),
});

export type WittySkillCreate = z.infer<typeof wittySkillCreateZod>;

export type WittySkillsApi = {
  listSkills(
    request: WittySkillListRequest,
  ): Promise<
    z.infer<
      ReturnType<typeof paginatedResponseZod<typeof wittySkillSummaryZod>>
    >
  >;
  getSkill(id: string): Promise<WittySkill>;
  updateSkill(id: string, update: WittySkillUpdate): Promise<WittySkill>;
  createSkill(data: WittySkillCreate): Promise<WittySkill>;
  deleteSkill(id: string, force?: boolean): Promise<void>;
};

export const wittySkillsApiRef: ApiRef<WittySkillsApi> =
  createApiRef<WittySkillsApi>({
    id: 'plugin.witty-skills',
  });

export class WittySkillsClient implements WittySkillsApi {
  constructor(
    private readonly fetchApi: FetchApi,
    private readonly discoveryApi: DiscoveryApi,
  ) {}

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('witty');
  }

  async listSkills(
    request: WittySkillListRequest,
  ): Promise<
    z.infer<
      ReturnType<typeof paginatedResponseZod<typeof wittySkillSummaryZod>>
    >
  > {
    const baseUrl = await this.getBaseUrl();
    const query = toQueryString({
      limit: request.limit,
      offset: request.offset,
      search: request.search,
      is_platform_provided: request.is_platform_provided,
      ids: request.ids,
      'sort-by': request.sort_by,
      'sort-order': request.sort_order,
    });
    const response = await this.fetchApi.fetch(`${baseUrl}/v1/skills${query}`);
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const data: unknown = await response.json();
    const parsed = paginatedResponseZod(wittySkillSummaryZod).safeParse(data);
    if (!parsed.success) {
      throw new Error(`Invalid skills response: ${parsed.error.message}`);
    }
    return parsed.data;
  }

  async getSkill(id: string): Promise<WittySkill> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/skills/${encodeURIComponent(id)}`,
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const data: unknown = await response.json();
    const parsed = wittySkillZod.safeParse(data);
    if (!parsed.success) {
      throw new Error(`Invalid skill response: ${parsed.error.message}`);
    }
    return parsed.data;
  }

  async updateSkill(id: string, update: WittySkillUpdate): Promise<WittySkill> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/skills/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(update),
      },
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const data: unknown = await response.json();
    const parsed = wittySkillZod.safeParse(data);
    if (!parsed.success) {
      throw new Error(`Invalid skill response: ${parsed.error.message}`);
    }
    return parsed.data;
  }

  async createSkill(skill: WittySkillCreate): Promise<WittySkill> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/v1/skills`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(skill),
    });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const data: unknown = await response.json();
    const parsed = wittySkillZod.safeParse(data);
    if (!parsed.success) {
      throw new Error(`Invalid skill response: ${parsed.error.message}`);
    }
    return parsed.data;
  }

  async deleteSkill(id: string, force = false): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const query = force ? '?force=true' : '';
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/skills/${encodeURIComponent(id)}${query}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }
}
